import React, { useEffect, useMemo, useState } from "react";
import {
  Select, Button, Table, Tag, Modal, Form, InputNumber, Input, DatePicker,
  Radio, Space, Typography, Popconfirm, Alert, Tabs, Empty, Tooltip,
} from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, CalendarOutlined, LinkOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { baseUrl } from "../../common/constants";
import _axios from "../../utils/api/_axios";
import { to12h } from "../../components/Availability/SlotPicker";

const { Option } = Select;
const { Title, Text } = Typography;

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Generate HH:MM options in 15-minute steps
const TIME_OPTIONS = (() => {
  const out = [];
  for (let m = 6 * 60; m <= 21 * 60; m += 15) {
    const h = Math.floor(m / 60), mm = m % 60;
    out.push(`${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
  }
  return out;
})();

const errMsg = (err) => err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || "Request failed";

const AvailabilityManagement = () => {
  const [physicians, setPhysicians] = useState([]);
  const [physicianId, setPhysicianId] = useState(null);
  const [offices, setOffices] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Schedule modal
  const [schedModalOpen, setSchedModalOpen] = useState(false);
  const [schedEditing, setSchedEditing] = useState(null);
  const [schedForm] = Form.useForm();

  // Exception modal
  const [excModalOpen, setExcModalOpen] = useState(false);
  const [excForm] = Form.useForm();

  // Calendar integration modal (groundwork)
  const [calModalOpen, setCalModalOpen] = useState(false);
  const [calInfo, setCalInfo] = useState(null);
  const [calForm] = Form.useForm();

  // Load physicians once
  useEffect(() => {
    _axios.get(`${baseUrl}/physician/get-all`)
      .then((r) => setPhysicians(r.data?.payload || []))
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const loadAll = async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const [off, sch, exc] = await Promise.all([
        _axios.get(`${baseUrl}/hospitalPhysician/physician/${pid}`),
        _axios.get(`${baseUrl}/availability/physician/${pid}/schedules`),
        _axios.get(`${baseUrl}/availability/physician/${pid}/exceptions`, {
          params: { from: dayjs().format("YYYY-MM-DD") },
        }),
      ]);
      setOffices(off.data?.payload || []);
      const s = sch.data?.payload;
      setSchedules(Array.isArray(s) ? s : []);
      const e = exc.data?.payload;
      setExceptions(Array.isArray(e) ? e : []);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(physicianId); }, [physicianId]);

  const physician = useMemo(() => physicians.find((p) => p._id === physicianId), [physicians, physicianId]);
  const officeName = (id) => offices.find((o) => o._id === id)?.hospitalName || "Unknown";

  // ── Weekly grid: group active schedules by day ──────────────
  const byDay = useMemo(() => {
    const m = {};
    for (let d = 0; d < 7; d++) m[d] = [];
    schedules.forEach((s) => { if (s.isActive !== false) m[s.dayOfWeek]?.push(s); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.startTime.localeCompare(b.startTime)));
    return m;
  }, [schedules]);

  // ── Schedule CRUD ───────────────────────────────────────────
  const openSchedModal = (existing = null, presetDay = null) => {
    setSchedEditing(existing);
    schedForm.resetFields();
    if (existing) {
      schedForm.setFieldsValue({
        hospitalId: existing.hospitalId,
        dayOfWeek: existing.dayOfWeek,
        startTime: existing.startTime,
        endTime: existing.endTime,
        slotDurationMinutes: existing.slotDurationMinutes || 30,
        effectiveFrom: existing.effectiveFrom ? dayjs(existing.effectiveFrom) : null,
        effectiveTo: existing.effectiveTo ? dayjs(existing.effectiveTo) : null,
        notes: existing.notes || "",
      });
    } else {
      schedForm.setFieldsValue({
        hospitalId: offices[0]?._id,
        dayOfWeek: presetDay ?? 1,
        startTime: "09:00",
        endTime: "17:00",
        slotDurationMinutes: 30,
      });
    }
    setSchedModalOpen(true);
  };

  const saveSchedule = async () => {
    try {
      const v = await schedForm.validateFields();
      const body = {
        physicianId,
        hospitalId: v.hospitalId,
        dayOfWeek: v.dayOfWeek,
        startTime: v.startTime,
        endTime: v.endTime,
        slotDurationMinutes: v.slotDurationMinutes,
        effectiveFrom: v.effectiveFrom ? v.effectiveFrom.format("YYYY-MM-DD") : null,
        effectiveTo: v.effectiveTo ? v.effectiveTo.format("YYYY-MM-DD") : null,
        notes: v.notes || "",
      };
      if (schedEditing) {
        await _axios.put(`${baseUrl}/availability/schedules/${schedEditing._id}`, body);
        toast.success("Schedule updated");
      } else {
        await _axios.post(`${baseUrl}/availability/schedules`, body);
        toast.success("Schedule added");
      }
      setSchedModalOpen(false);
      loadAll(physicianId);
    } catch (e) {
      if (e?.errorFields) return; // antd validation
      toast.error(errMsg(e));
    }
  };

  const deleteSchedule = async (id) => {
    try {
      await _axios.delete(`${baseUrl}/availability/schedules/${id}`);
      toast.success("Schedule removed");
      loadAll(physicianId);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  // ── Exception CRUD ──────────────────────────────────────────
  const openExcModal = () => {
    excForm.resetFields();
    excForm.setFieldsValue({ type: "block", scope: "all", fullDay: true, date: dayjs() });
    setExcModalOpen(true);
  };

  const saveException = async () => {
    try {
      const v = await excForm.validateFields();
      const body = {
        physicianId,
        hospitalId: v.type === "add" ? v.hospitalId : (v.scope === "all" ? null : v.hospitalId),
        date: v.date.format("YYYY-MM-DD"),
        type: v.type,
        startTime: v.type === "add" || !v.fullDay ? v.startTime : null,
        endTime: v.type === "add" || !v.fullDay ? v.endTime : null,
        slotDurationMinutes: v.type === "add" ? v.slotDurationMinutes || 30 : null,
        reason: v.reason || "",
      };
      await _axios.post(`${baseUrl}/availability/exceptions`, body);
      toast.success(v.type === "block" ? "Time blocked" : "Extra hours added");
      setExcModalOpen(false);
      loadAll(physicianId);
    } catch (e) {
      if (e?.errorFields) return;
      toast.error(errMsg(e));
    }
  };

  const deleteException = async (id) => {
    try {
      await _axios.delete(`${baseUrl}/availability/exceptions/${id}`);
      toast.success("Removed");
      loadAll(physicianId);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  // ── Calendar integration (groundwork) ───────────────────────
  const openCalModal = async () => {
    try {
      const r = await _axios.get(`${baseUrl}/availability/physician/${physicianId}/calendar`);
      const info = r.data?.payload;
      setCalInfo(info);
      calForm.setFieldsValue({
        provider: info?.integration?.provider || null,
        externalCalendarId: info?.integration?.externalCalendarId || "",
        syncEnabled: !!info?.integration?.syncEnabled,
        syncDirection: info?.integration?.syncDirection || "both",
      });
      setCalModalOpen(true);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveCal = async () => {
    try {
      const v = await calForm.validateFields();
      await _axios.put(`${baseUrl}/availability/physician/${physicianId}/calendar`, v);
      toast.success("Calendar settings saved");
      setCalModalOpen(false);
    } catch (e) {
      if (e?.errorFields) return;
      toast.error(errMsg(e));
    }
  };

  // ── Render ──────────────────────────────────────────────────
  const excColumns = [
    { title: "Date", dataIndex: "date", key: "date", render: (d) => dayjs(d).format("ddd MMM D, YYYY") },
    {
      title: "Type", dataIndex: "type", key: "type",
      render: (t) => t === "block" ? <Tag color="red">Blocked</Tag> : <Tag color="green">Extra hours</Tag>,
    },
    {
      title: "Time", key: "time",
      render: (_, r) => r.startTime ? `${to12h(r.startTime)} – ${to12h(r.endTime)}` : <Text type="secondary">All day</Text>,
    },
    { title: "Facility", dataIndex: "hospitalName", key: "hospitalName" },
    { title: "Reason", dataIndex: "reason", key: "reason", render: (r) => r || <Text type="secondary">—</Text> },
    {
      title: "", key: "actions", width: 60,
      render: (_, r) => (
        <Popconfirm title="Remove this exception?" onConfirm={() => deleteException(r._id)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const excType = Form.useWatch("type", excForm);
  const excFullDay = Form.useWatch("fullDay", excForm);
  const excScope = Form.useWatch("scope", excForm);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <Title level={3} style={{ margin: 0 }}>Physician Availability</Title>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <Space size="large" wrap>
          <div>
            <Text strong>Physician</Text>
            <Select
              showSearch
              style={{ width: 340, display: "block", marginTop: 4 }}
              placeholder="Select a physician"
              value={physicianId}
              onChange={setPhysicianId}
              optionFilterProp="children"
              filterOption={(input, opt) => (opt?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())}
            >
              {physicians.map((p) => (
                <Option key={p._id} value={p._id}>
                  {p.physicianPrefix} {p.physicianFirstName} {p.physicianLastName} — {p.specialization}
                </Option>
              ))}
            </Select>
          </div>
          {physician && (
            <div style={{ paddingTop: 22 }}>
              <Space wrap>
                {offices.map((o) => <Tag key={o._id} color="blue">{o.hospitalName}</Tag>)}
                {offices.length === 0 && <Text type="secondary">Not assigned to any facility</Text>}
              </Space>
            </div>
          )}
          {physician && (
            <div style={{ paddingTop: 22 }}>
              <Tooltip title="Connect Google / Outlook calendar (coming soon)">
                <Button icon={<LinkOutlined />} onClick={openCalModal}>Calendar sync</Button>
              </Tooltip>
            </div>
          )}
        </Space>
      </div>

      {!physicianId ? (
        <Empty description="Select a physician to manage their availability" />
      ) : offices.length === 0 ? (
        <Alert type="warning" showIcon message="This physician isn't assigned to any facility yet. Assign them under Facility → Physicians first." />
      ) : (
        <Tabs
          defaultActiveKey="weekly"
          items={[
            {
              key: "weekly",
              label: <span><CalendarOutlined /> Weekly schedule</span>,
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    className="mb-4"
                    message="A physician's hours cannot overlap across facilities on the same day. Back-to-back blocks (e.g. 9–12 at one office, 12–5 at another) are allowed."
                  />
                  <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
                    {DAYS.map((name, d) => (
                      <div key={d} className="bg-white rounded-lg shadow p-3 min-h-[180px] flex flex-col">
                        <div className="flex items-center justify-between mb-2">
                          <Text strong>{name}</Text>
                          <Button size="small" type="text" icon={<PlusOutlined />} onClick={() => openSchedModal(null, d)} />
                        </div>
                        <div className="flex-1 space-y-2">
                          {byDay[d].length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>Off</Text>}
                          {byDay[d].map((s) => (
                            <div key={s._id} className="border rounded p-2 text-xs bg-blue-50 border-blue-200">
                              <div className="font-semibold">{to12h(s.startTime)} – {to12h(s.endTime)}</div>
                              <div className="text-gray-600 truncate" title={officeName(s.hospitalId)}>{officeName(s.hospitalId)}</div>
                              <div className="text-gray-500">{s.slotDurationMinutes || 30} min slots</div>
                              {(s.effectiveFrom || s.effectiveTo) && (
                                <div className="text-gray-500">
                                  {s.effectiveFrom ? dayjs(s.effectiveFrom).format("M/D/YY") : "…"} → {s.effectiveTo ? dayjs(s.effectiveTo).format("M/D/YY") : "…"}
                                </div>
                              )}
                              {s.source && s.source !== "manual" && <Tag color="purple" style={{ marginTop: 4 }}>{s.source}</Tag>}
                              <div className="flex gap-1 mt-1">
                                <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openSchedModal(s)} />
                                <Popconfirm title="Remove this block?" onConfirm={() => deleteSchedule(s._id)}>
                                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ),
            },
            {
              key: "exceptions",
              label: "Time off & extra hours",
              children: (
                <>
                  <div className="flex justify-end mb-3">
                    <Button type="primary" icon={<PlusOutlined />} onClick={openExcModal}>Add exception</Button>
                  </div>
                  <Table
                    rowKey="_id"
                    size="small"
                    loading={loading}
                    columns={excColumns}
                    dataSource={exceptions}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: "No upcoming time off or extra hours" }}
                  />
                </>
              ),
            },
          ]}
        />
      )}

      {/* ── Schedule modal ── */}
      <Modal
        title={schedEditing ? "Edit weekly block" : "Add weekly block"}
        open={schedModalOpen}
        onOk={saveSchedule}
        onCancel={() => setSchedModalOpen(false)}
        okText="Save"
        destroyOnClose
      >
        <Form form={schedForm} layout="vertical">
          <Form.Item name="hospitalId" label="Facility" rules={[{ required: true }]}>
            <Select>{offices.map((o) => <Option key={o._id} value={o._id}>{o.hospitalName}</Option>)}</Select>
          </Form.Item>
          <Form.Item name="dayOfWeek" label="Day" rules={[{ required: true }]}>
            <Select>{DAYS.map((n, i) => <Option key={i} value={i}>{n}</Option>)}</Select>
          </Form.Item>
          <Space size="middle" style={{ display: "flex" }}>
            <Form.Item name="startTime" label="Start" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Select showSearch>{TIME_OPTIONS.map((t) => <Option key={t} value={t}>{to12h(t)}</Option>)}</Select>
            </Form.Item>
            <Form.Item name="endTime" label="End" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Select showSearch>{TIME_OPTIONS.map((t) => <Option key={t} value={t}>{to12h(t)}</Option>)}</Select>
            </Form.Item>
            <Form.Item name="slotDurationMinutes" label="Slot length (min)" rules={[{ required: true }]} style={{ flex: 1 }}>
              <InputNumber min={5} max={480} step={5} style={{ width: "100%" }} />
            </Form.Item>
          </Space>
          <Space size="middle" style={{ display: "flex" }}>
            <Form.Item name="effectiveFrom" label="Effective from (optional)" style={{ flex: 1 }}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="effectiveTo" label="Effective to (optional)" style={{ flex: 1 }}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Notes"><Input /></Form.Item>
        </Form>
      </Modal>

      {/* ── Exception modal ── */}
      <Modal
        title="Add exception"
        open={excModalOpen}
        onOk={saveException}
        onCancel={() => setExcModalOpen(false)}
        okText="Save"
        destroyOnClose
      >
        <Form form={excForm} layout="vertical">
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="block">Block time off</Radio.Button>
              <Radio.Button value="add">Add extra hours</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          {excType === "block" && (
            <>
              <Form.Item name="scope" label="Applies to">
                <Radio.Group>
                  <Radio value="all">All facilities (out of office)</Radio>
                  <Radio value="one">One facility only</Radio>
                </Radio.Group>
              </Form.Item>
              {excScope === "one" && (
                <Form.Item name="hospitalId" label="Facility" rules={[{ required: true }]}>
                  <Select>{offices.map((o) => <Option key={o._id} value={o._id}>{o.hospitalName}</Option>)}</Select>
                </Form.Item>
              )}
              <Form.Item name="fullDay" label="Duration">
                <Radio.Group>
                  <Radio value={true}>Full day</Radio>
                  <Radio value={false}>Part of the day</Radio>
                </Radio.Group>
              </Form.Item>
            </>
          )}

          {excType === "add" && (
            <Form.Item name="hospitalId" label="Facility" rules={[{ required: true }]}>
              <Select>{offices.map((o) => <Option key={o._id} value={o._id}>{o.hospitalName}</Option>)}</Select>
            </Form.Item>
          )}

          {(excType === "add" || excFullDay === false) && (
            <Space size="middle" style={{ display: "flex" }}>
              <Form.Item name="startTime" label="Start" rules={[{ required: true }]} style={{ flex: 1 }}>
                <Select showSearch>{TIME_OPTIONS.map((t) => <Option key={t} value={t}>{to12h(t)}</Option>)}</Select>
              </Form.Item>
              <Form.Item name="endTime" label="End" rules={[{ required: true }]} style={{ flex: 1 }}>
                <Select showSearch>{TIME_OPTIONS.map((t) => <Option key={t} value={t}>{to12h(t)}</Option>)}</Select>
              </Form.Item>
              {excType === "add" && (
                <Form.Item name="slotDurationMinutes" label="Slot (min)" style={{ flex: 1 }}>
                  <InputNumber min={5} max={480} step={5} style={{ width: "100%" }} />
                </Form.Item>
              )}
            </Space>
          )}

          <Form.Item name="reason" label="Reason"><Input placeholder="Vacation, CME, office closed…" /></Form.Item>
        </Form>
      </Modal>

      {/* ── Calendar integration modal (groundwork) ── */}
      <Modal
        title="External calendar sync"
        open={calModalOpen}
        onOk={saveCal}
        onCancel={() => setCalModalOpen(false)}
        okText="Save"
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Groundwork only"
          description="Provider settings are stored now. Live sync with Google / Outlook will activate once OAuth is connected in a future release."
        />
        <Form form={calForm} layout="vertical">
          <Form.Item name="provider" label="Provider">
            <Select allowClear placeholder="None">
              {(calInfo?.availableProviders || []).filter((p) => p !== "manual").map((p) => (
                <Option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="externalCalendarId" label="Calendar ID" tooltip='For Google use "primary" or a specific calendar ID'>
            <Input placeholder="primary" />
          </Form.Item>
          <Form.Item name="syncDirection" label="Sync direction">
            <Select>
              <Option value="import">Import busy times only</Option>
              <Option value="export">Push appointments only</Option>
              <Option value="both">Both</Option>
            </Select>
          </Form.Item>
          <Form.Item name="syncEnabled" label="Enabled" valuePropName="checked">
            <Radio.Group>
              <Radio value={true}>On</Radio>
              <Radio value={false}>Off</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AvailabilityManagement;
